// Bottom of the rail: who is signed in, with Account, Appearance (System / Light / Dark, ui/theme.ts) and Sign out
// (iOS MoreMenuSheet "Account" group). Sign out
// goes through useAuth().signOut(), which first pushes pending edits and then wipes local data.
// The name and avatar color come from the member's own `users` item, as MoreMenuProfileHeader shows them
// (User.fullName, Theme.color(hex: colorHex)); until that row has synced, from the auth account (accountLabel.ts).

import { ChevronsUpDown, LogOut, UserRound } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '@/auth/AuthProvider';
import { useDecodedRow } from '@/sync/hooks';
import {
  Avatar,
  DropdownMenu,
  THEME_ICONS,
  THEME_LABELS,
  THEME_PREFERENCES,
  cn,
  useTheme,
  useToast,
  type MenuEntry,
} from '@/ui';
import { accountDisplayName, profileLook } from './accountLabel';

export { accountDisplayName, profileLook, type ProfileLook } from './accountLabel';

export function AccountMenu({ compact = false }: { compact?: boolean }) {
  const { user, session, signOut } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [signingOut, setSigningOut] = useState(false);
  const theme = useTheme();

  const profile = useDecodedRow('users', session?.profileId, profileLook);

  const email = session?.email ?? user?.email ?? null;
  const name = profile?.name || accountDisplayName(user, email);
  const colorHex = profile?.colorHex ?? null;

  const doSignOut = async () => {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await signOut();
    } catch (error) {
      toast.error(error instanceof Error && error.message ? error.message : "Couldn't sign out. Try again.", {
        title: 'Sign out failed',
      });
      setSigningOut(false);
    }
  };

  const header = (
    <div className="flex items-center gap-2.5">
      <Avatar name={name} colorHex={colorHex} size="lg" label={null} />
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-fg">{name}</p>
        {email && <p className="truncate text-xs text-fg-muted">{email}</p>}
      </div>
    </div>
  );

  return (
    <DropdownMenu
      label="Account"
      placement={compact ? 'right-end' : 'top-start'}
      minWidth={240}
      header={header}
      items={[
        { id: 'account', label: 'Account', icon: <UserRound />, onSelect: () => navigate('/account') },
        { type: 'separator', id: 'sep-appearance' },
        { type: 'label', id: 'appearance', label: 'Appearance', group: true },
        ...THEME_PREFERENCES.map(
          (value): MenuEntry => ({
            id: `theme-${value}`,
            label: THEME_LABELS[value],
            icon: THEME_ICONS[value],
            checked: theme.preference === value,
            radio: true,
            onSelect: () => theme.setPreference(value),
          }),
        ),
        { type: 'separator', id: 'sep' },
        {
          id: 'sign-out',
          label: signingOut ? 'Signing out…' : 'Sign out',
          textValue: 'Sign out',
          icon: <LogOut />,
          disabled: signingOut,
          onSelect: () => void doSignOut(),
        },
      ]}
      trigger={
        <button
          type="button"
          aria-label={compact ? `Account: ${name}` : undefined}
          title={compact ? name : undefined}
          className={cn(
            'flex w-full min-w-0 items-center gap-2.5 rounded-tile text-left transition-colors hover:bg-surface-3/70',
            'aria-expanded:bg-surface-3/70',
            compact ? 'justify-center p-1.5' : 'px-2 py-2',
          )}
        >
          <Avatar name={name} colorHex={colorHex} size="md" label={null} />
          {!compact && (
            <>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-fg">{name}</span>
                {email && <span className="block truncate text-xs text-fg-muted">{email}</span>}
              </span>
              <ChevronsUpDown aria-hidden className="size-4 shrink-0 text-fg-subtle" />
            </>
          )}
        </button>
      }
    />
  );
}
